<script setup>
// new-conversation-modal.GALLERY.2
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ inbox: { type: Object, required: true } });
const emit = defineEmits(['created', 'back']);
const store = useStore();
const { t } = useI18n();
const form = reactive({
  name: '',
  language: 'es_AR',
  header: '',
  body: '',
  footer: '',
});
const submitting = ref(false);
const variableExample = '{{name}}';
const valid = computed(
  () =>
    /^[a-z][a-z0-9_]*$/.test(form.name) &&
    form.language.trim().length > 0 &&
    form.body.trim().length > 0 &&
    !/\{\{\d+\}\}/.test(form.body) &&
    !form.header.includes('{{') &&
    !form.footer.includes('{{')
);

const submit = async () => {
  if (!valid.value || submitting.value) return;
  submitting.value = true;
  try {
    const result = await store.dispatch('whatsappTemplates/create', {
      inboxId: props.inbox.id,
      templateData: {
        name: form.name,
        language: form.language,
        body: form.body,
        header_type: form.header ? 'text' : 'none',
        header_text: form.header || undefined,
        footer: form.footer || undefined,
      },
    });
    store.dispatch('inboxes/get').catch(() => {});
    emit('created', {
      name: form.name,
      language: form.language,
      category: 'MARKETING',
      id: result?.template?.id,
      status: result?.template?.status || 'PENDING',
      parameter_format: 'NAMED',
      components: [
        ...(form.header
          ? [{ type: 'HEADER', format: 'TEXT', text: form.header }]
          : []),
        { type: 'BODY', text: form.body },
        ...(form.footer ? [{ type: 'FOOTER', text: form.footer }] : []),
      ],
    });
  } catch (error) {
    useAlert(error?.response?.data?.error || t('NEW_CONVERSATION_MODAL.ERROR'));
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-3"
    data-testid="create-template-step"
    @submit.prevent="submit"
  >
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.CREATE') }}
        </h3>
        <p class="text-xs text-n-slate-10">
          {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.CREATE_HINT') }}
        </p>
      </div>
      <Button
        type="button"
        :label="t('NEW_CONVERSATION_MODAL.BACK')"
        color="slate"
        size="sm"
        @click="emit('back')"
      />
    </div>
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-xs text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.NAME') }}
        <input
          v-model="form.name"
          class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg border-n-weak text-n-slate-12"
          maxlength="512"
          :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.NAME_EXAMPLE')"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.LANGUAGE') }}
        <input
          v-model="form.language"
          class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg border-n-weak text-n-slate-12"
          :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.LANGUAGE_EXAMPLE')"
        />
      </label>
    </div>
    <label class="flex flex-col gap-1 text-xs text-n-slate-10">
      {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.HEADER') }}
      <input
        v-model="form.header"
        maxlength="60"
        class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg border-n-weak text-n-slate-12"
      />
    </label>
    <label class="flex flex-col gap-1 text-xs text-n-slate-10">
      {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.BODY') }}
      <textarea
        v-model="form.body"
        maxlength="1024"
        rows="6"
        class="px-3 py-2 text-sm bg-n-solid-1 border rounded-lg resize-none border-n-weak text-n-slate-12"
      />
    </label>
    <p class="text-xs text-n-slate-10">
      {{
        t('NEW_CONVERSATION_MODAL.TEMPLATE.VARIABLE_HINT', {
          variable: variableExample,
        })
      }}
    </p>
    <label class="flex flex-col gap-1 text-xs text-n-slate-10">
      {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.FOOTER') }}
      <input
        v-model="form.footer"
        maxlength="60"
        class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg border-n-weak text-n-slate-12"
      />
    </label>
    <div class="flex justify-end">
      <Button
        type="submit"
        :label="t('NEW_CONVERSATION_MODAL.TEMPLATE.SUBMIT')"
        :is-loading="submitting"
        :disabled="!valid"
        size="sm"
      />
    </div>
  </form>
</template>
